import { useState } from 'react'

export interface PlateConfig {
  substrate: { height: number; bottom: number }
  plate: { width: number; bottom: number }
  plateTop: { width: number }
  plateBase: { width: number; marginTop: number }
  plateStripe: { width: number; marginTop: number }
  saucePlate: { width: number; left: string; bottom: number }
}

interface PlateEditorProps {
  config: PlateConfig
  onChange: (config: PlateConfig) => void
}

export const defaultPlateConfig: PlateConfig = {
  substrate: { height: 40, bottom: 0 },
  plate: { width: 189, bottom: 0 },        // -15% (было 222)
  plateTop: { width: 0 },
  plateBase: { width: 292, marginTop: -50 }, // -15% (было 343)
  plateStripe: { width: 328, marginTop: 0 }, // -15% (было 386)
  saucePlate: { width: 42, left: '14%', bottom: 23 }, // -15% ширина
}

export default function PlateEditor({ config, onChange }: PlateEditorProps) {
  const [isOpen, setIsOpen] = useState(false)

  const updateConfig = (
    section: keyof PlateConfig,
    field: string,
    value: number | string
  ) => {
    onChange({
      ...config,
      [section]: {
        ...config[section],
        [field]: value,
      },
    })
  }

  const exportConfig = () => {
    const json = JSON.stringify(config, null, 2)
    // Config copied to clipboard
    navigator.clipboard.writeText(json)
    alert('Конфиг скопирован в буфер обмена!')
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-32 z-50 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-bold shadow-lg"
      >
        🍽️ Тарелка
      </button>
    )
  }

  return (
    <div className="fixed bottom-4 left-32 z-50 bg-gray-900/95 text-white p-4 rounded-xl shadow-2xl w-[320px] max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-lg">🍽️ Настройки тарелки</h3>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white text-xl">✕</button>
      </div>

      {/* Подложка */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-blue-400">📦 Подложка (substrate)</h4>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Высота
            <input
              type="number"
              value={config.substrate.height}
              onChange={(e) => updateConfig('substrate', 'height', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Bottom
            <input
              type="number"
              value={config.substrate.bottom}
              onChange={(e) => updateConfig('substrate', 'bottom', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
        </div>
      </div>

      {/* Тарелка (контейнер) */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-green-400">🍽️ Тарелка (контейнер)</h4>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Ширина
            <input
              type="number"
              value={config.plate.width}
              onChange={(e) => updateConfig('plate', 'width', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Bottom
            <input
              type="number"
              value={config.plate.bottom}
              onChange={(e) => updateConfig('plate', 'bottom', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
        </div>
      </div>

      {/* PlateTop */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-yellow-400">✨ PlateTop (верх)</h4>
        <label className="text-xs">
          Ширина
          <input
            type="number"
            value={config.plateTop.width}
            onChange={(e) => updateConfig('plateTop', 'width', Number(e.target.value))}
            className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
          />
        </label>
      </div>

      {/* PlateBase */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-orange-400">🟤 PlateBase (основание)</h4>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Ширина
            <input
              type="number"
              value={config.plateBase.width}
              onChange={(e) => updateConfig('plateBase', 'width', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Margin-top
            <input
              type="number"
              value={config.plateBase.marginTop}
              onChange={(e) => updateConfig('plateBase', 'marginTop', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
        </div>
      </div>

      {/* PlateStripe */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-gray-400">⬛ PlateStripe (полоска)</h4>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            Ширина
            <input
              type="number"
              value={config.plateStripe.width}
              onChange={(e) => updateConfig('plateStripe', 'width', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Margin-top
            <input
              type="number"
              value={config.plateStripe.marginTop}
              onChange={(e) => updateConfig('plateStripe', 'marginTop', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
        </div>
      </div>

      {/* SaucePlate */}
      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
        <h4 className="font-semibold mb-2 text-red-400">🥣 Соусница</h4>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-xs">
            Ширина
            <input
              type="number"
              value={config.saucePlate.width}
              onChange={(e) => updateConfig('saucePlate', 'width', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Left
            <input
              type="text"
              value={config.saucePlate.left}
              onChange={(e) => updateConfig('saucePlate', 'left', e.target.value)}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
          <label className="text-xs">
            Bottom
            <input
              type="number"
              value={config.saucePlate.bottom}
              onChange={(e) => updateConfig('saucePlate', 'bottom', Number(e.target.value))}
              className="w-full bg-gray-700 rounded px-2 py-1 mt-1"
            />
          </label>
        </div>
      </div>

      {/* Кнопки */}
      <div className="flex gap-2">
        <button
          onClick={exportConfig}
          className="flex-1 bg-green-600 hover:bg-green-500 px-3 py-2 rounded font-bold"
        >
          📋 Экспорт
        </button>
        <button
          onClick={() => onChange(defaultPlateConfig)}
          className="flex-1 bg-gray-600 hover:bg-gray-500 px-3 py-2 rounded font-bold"
        >
          🔄 Сброс
        </button>
      </div>
    </div>
  )
}

